import { useSignupPage, signupErrorReason } from 'utils/signupAnalytics';
import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, {ChangeEvent, useEffect, useRef, useState} from 'react'
import {useHistory, useLocation} from 'react-router';
import {
    FormControlLabel,
    Checkbox,
    FormControl,
    MenuItem,
} from "@mui/material";
import {
    Page,
    Container, ChangeRoleBlock, ContinueButton, InputPhoneBlock, NameBlock
} from "./SignUp1_DO.styles";
import * as ls from "utils/localStorage";
import MaterialInput from "../../../components/Inputs/MaterialInput";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import {isNumber} from "lodash";
import Api from "../../../api";
import {BodyContainer, BodyCountrySelect, HeaderCountrySelect, CountrySelectBlock} from "../SignUp1_Talent/SignUp1_Talent.styles";
import {Container as HeaderContainer, StarsContainer} from "components/SignUpHeader/SignUpHeader.styles";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import useActionApi from "../../../hooks/useActionApi";
import { MARKETING_SOURCES } from "../../../utils/constants";
import { buildOfficeContactInfoErrorPayload } from "./officeContactInfoAnalytics";
import { normalizeSignupText } from '../../../utils/signupText';
import {
    buildCompletedOfficeAddress,
    getOfficeAddressValidationError,
    normalizeUnitSuite,
    OfficeAutocompleteAddress,
    OfficeAutocompleteEvent,
} from "./officeRegistrationAddress";
// @ts-ignore
const TempStars: any = window.TempStars;

interface ErrorProps {
    practiceName: string,
    ownersName: string,
    phone: string,
    address: string,
    postalCode: string,
    country: string,
    document: string,
    marketingSource: string,
    otherSource: string,
    dsoAffiliation: string,
}

export type AutocompleteProps = OfficeAutocompleteAddress;

export interface completeRegistrationDoProps {
    practiceName: string,
    ownersName: string,
    phone: number,
    country: 0 | 1,
    autocomplete?: AutocompleteProps,
    addressInput?: string,
    unitSuite?: string,
    postalCode?: string,
    photoUrl?: string,
    marketingSource: string,
    otherSource?: string,
    dsoAffiliation?: string,
    addressError?: string,
}

const getOfficePhoneDigits = (value?: string): string => {
    const rawValue = value || '';
    let digits = rawValue.replace(/\D/g, '');
    if (digits.startsWith('1') && (rawValue.trim().startsWith('+1') || digits.length > 10)) {
        digits = digits.slice(1);
    }
    return digits.slice(0, 10);
}

const filterPhoneNumberToFormat = (phoneNumber: string | undefined): number =>
    Number(getOfficePhoneDigits(phoneNumber));

export const formatOfficePhoneInput = (value?: string): string => {
    const digits = getOfficePhoneDigits(value);

    if (!digits) {
        return '';
    }

    let formatted = `+1(${digits.slice(0, 3)}`;
    if (digits.length > 3) {
        formatted += `) ${digits.slice(3, 6)}`;
    }
    if (digits.length > 6) {
        formatted += `-${digits.slice(6)}`;
    }
    return formatted;
}

const formatOfficePhoneWhileTyping = (value?: string): string => {
    const digits = getOfficePhoneDigits(value);
    return digits.length === 10 ? formatOfficePhoneInput(digits) : digits;
}

interface SignUp1HeaderProps {
    title?: string,
    description?: string,
    error?: string
}

const SignUp1Header = ({title, description, error}: SignUp1HeaderProps) => {
    return <HeaderContainer>
        <div>
            <div className="screenDescription">
                {description}
            </div>
            <div className="screenTitle">
                {title}
            </div>
            {error && <div className="screenError">
                {error}
            </div>}
        </div>
        <div><StarsContainer/></div>
    </HeaderContainer>
}

export default function SignUp1_DO() {
    const history = useHistory();
    const signup = useSignupPage('office_info', 'office');
    const { state } = useLocation<completeRegistrationDoProps>();

    const [country, setCountry] = useState<0 | 1>(isNumber(state?.country) ? state.country : 1); // 0 - CA
    const [screenError, setScreenError] = useState(!!state?.addressError);
    const [autocompletedAddress, setAutocompletedAddress] = useState<AutocompleteProps | undefined>(state?.autocomplete);
    const [addressInput, setAddressInput] = useState(
        state?.addressInput || state?.autocomplete?.formattedAddress || state?.autocomplete?.address || '',
    );
    const [unitSuite, setUnitSuite] = useState(state?.unitSuite || state?.autocomplete?.subpremise || '');
    const [postalCode, setPostalCode] = useState(state?.postalCode || state?.autocomplete?.postalCode || '');
    const [phoneInput, setPhoneInput] = useState(formatOfficePhoneInput(state?.phone ? String(state.phone) : ''));
    const [addressServiceError, setAddressServiceError] = useState<string | undefined>();
    const [errorMessage, setErrorMessage] = useState<Partial<ErrorProps> | undefined>(
        state?.addressError ? {address: state.addressError} : undefined,
    );
    const htmlElRef = useRef<HTMLInputElement>(null)
    const [loading, setLoading] = useState<boolean>(false);
    const [marketingSource, setMarketingSource] = useState<string>(state?.marketingSource || '');
    const [otherSource, setOtherSource] = useState<string>(state?.otherSource || '');
    const [dsoAffiliation, setDsoAffiliation] = useState<string>(state?.dsoAffiliation || '');
    const analyticsTracked = useRef(false);

    useEffect(() => {
        const checkUser = async () => {
            const userAuth = ls.get('userAuth');
            if (userAuth) {
                const userAccount = await Api.getUser();
                if (userAccount && userAccount.dentistId && userAccount.dentist && userAccount.dentist.isComplete) {
                    history.push({pathname: 'jobBoard'});
                }
            }
        };
        checkUser();
    }, [history]);

    const [changeRoleLoading, , changeRole] = useActionApi({
        apiMethod: Api.changeRole,
        onSuccess: () => { signup.switched('talent'); history.push({pathname: 'SignUp1_Talent'}); },
        onError: (error) => signup.error(signupErrorReason(error)),
    });

    useEffect(() => {
        TempStars.Autocomplete.init({country},(event: OfficeAutocompleteEvent) => {
            if (event.eventType === 'input') {
                setAutocompletedAddress(undefined);
                setAddressInput(event.inputValue || '');
                setAddressServiceError(undefined);
                setErrorMessage((current) => current ? {...current, address: undefined} : current);
                return;
            }

            if (event.eventType === 'error') {
                const message = event.errorMessage || 'Address search is temporarily unavailable. Please try again.';
                setAutocompletedAddress(undefined);
                setAddressServiceError(message);
                setErrorMessage((current) => ({...current, address: message}));
                setScreenError(true);
                return;
            }

            const {state, city, postalCode, address, baseAddress, formattedAddress, buildingNumber, subpremise, valueEqual} = event;
            if (!address && !buildingNumber && !city) {
                setAutocompletedAddress(undefined);
            } else if (valueEqual) {
                setAutocompletedAddress({
                    state,
                    city,
                    postalCode,
                    address: baseAddress || address,
                    baseAddress,
                    formattedAddress,
                    buildingNumber,
                    subpremise,
                    valueEqual,
                });
                setAddressInput(formattedAddress || address || '');
                setPostalCode(postalCode || '');
                setAddressServiceError(undefined);
                setErrorMessage((current) => current ? {...current, address: undefined} : current);
                if (subpremise) {
                    setUnitSuite(subpremise);
                }
            }
        }, {emitLifecycleEvents: true});
        
        return () => {
            TempStars.Autocomplete.removeListener();
        };
    }, [country])

    useEffect(() => {
        if (!analyticsTracked.current) {
            TempStars.Analytics.track('view_office_createAccount', {
                country: country === 0 ? 'CA' : 'US'
            });
            analyticsTracked.current = true;
        }
    }, [country]);

    const changeCountry = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault()
        if (event.target.name === 'us') {
            setCountry(1)
        } else {
            setCountry(0)
        }
        setAutocompletedAddress(undefined);
        setAddressInput('');
        setUnitSuite('');
        setPostalCode('');
        setAddressServiceError(undefined);
        setErrorMessage((current) => current ? {...current, address: undefined} : current);
    }

    const handleAddressInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAddressInput(event.target.value);
        setAutocompletedAddress(undefined);
        setAddressServiceError(undefined);
        setErrorMessage((current) => current ? {...current, address: undefined} : current);
    }

    const onSubmit = async (event: any) => {
        event.preventDefault()
        setLoading(true);

        const {practiceName, ownersName, phone, address} = event.target

        let errors: Partial<ErrorProps> = {}

        if (!normalizeSignupText(practiceName.value)) {
            errors = {...errors, practiceName: 'Please enter Practice Name'};
        }
        if (!normalizeSignupText(ownersName.value)) {
            errors = {...errors, ownersName: "Please enter Owner's Full Name"};
        }
        if (getOfficePhoneDigits(phone.value).length < 10) {
            errors = {...errors, phone: 'Please enter Mobile'};
        }
        
        const completedAutocomplete = autocompletedAddress ? {
            ...autocompletedAddress,
            postalCode: postalCode.trim(),
        } : undefined;
        const addressError = addressServiceError || getOfficeAddressValidationError(address.value, completedAutocomplete);
        if (addressError) {
            errors = {...errors, address: addressError};
        }
        
        if (country !== 0 && country !== 1) {
            errors = {...errors, country: 'Please choose your country'};
        }
        if (!marketingSource) {
            errors = {...errors, marketingSource: 'Please select how you heard about us'};
        }
        if (marketingSource === 'other' && !normalizeSignupText(otherSource)) {
            errors = {...errors, otherSource: 'Please specify how you heard about us'};
        }
        if (marketingSource.length > 150) {
            errors = {...errors, marketingSource: 'Response too long. Maximum 150 characters.'};
        }

        const phoneNumber = filterPhoneNumberToFormat(phone.value);

        if (phoneNumber.toString().length < 10) {
            errors = {...errors, phone: 'Phone number is too short.'};
        }

        let phoneDuplicate = false;
        let phoneApiFailed = false;

        if (!errors.phone) {
            try {
                const isUnique = await Api.isPhoneNumberUnique(phoneNumber);
                if (!isUnique) {
                    phoneDuplicate = true;
                    errors = {...errors, phone: 'This phone number is already associated with an account.'};
                }
            } catch (e) {
                phoneApiFailed = true;
                errors = {
                    ...errors,
                    phone: 'Unable to verify phone number. Please try again.',
                };
                console.error(e);
            }
        }

        if(Object.values(errors).find((err: any) => !!err.length)) {
            setErrorMessage(errors as ErrorProps);
            setLoading(false);
            TempStars.Analytics.track(
                'sign-up - office contact info error',
                buildOfficeContactInfoErrorPayload({
                    errors: errors as Record<string, string | undefined>,
                    practiceName: practiceName.value,
                    ownersName: ownersName.value,
                    phone: phone.value,
                    addressTyped: address.value,
                    hasAutocomplete: !!autocompletedAddress,
                    marketingSource,
                    dsoAffiliation,
                    country,
                    phoneDuplicate,
                    phoneApiFailed,
                })
            );
            signup.error(phoneApiFailed ? 'phone_verification_failed' : 'validation_failed');
            return setScreenError(true);
        } else {
            setErrorMessage(undefined);
            setScreenError(false);
        }

        TempStars.Analytics.track( 'sign-up - complete dental office form');
        setLoading(false);
        
        const props: completeRegistrationDoProps = {
            practiceName: normalizeSignupText(practiceName.value),
            ownersName: normalizeSignupText(ownersName.value),
            phone: phoneNumber,
            country: country,
            autocomplete: buildCompletedOfficeAddress(completedAutocomplete!, unitSuite),
            addressInput: address.value,
            unitSuite: normalizeUnitSuite(unitSuite),
            postalCode: postalCode.trim(),
            photoUrl: '',
            marketingSource: normalizeSignupText(marketingSource),
            otherSource: normalizeSignupText(otherSource) || undefined,
            dsoAffiliation: normalizeSignupText(dsoAffiliation) || undefined
        }

        history.push({
            pathname: 'SignUp2_DO'
        }, props);
        signup.submitted();
    };

    const changeRoleToTalent = async () => {
        TempStars.Analytics.track('Select-Route', {
            route_selected: 'UserToTalent'
        });
        TempStars.Analytics.track('sign-up - office contact info switch to talent clicked', {
            source_flow: 'office_signup',
            source_step_number: 3,
            source_step_name: 'office_contact_info',
            destination_flow: 'talent_signup',
            destination_page: 'SignUp1_Talent',
            country: country === 1 ? 'US' : 'CA',
        });
        await changeRole(null);
    }

    const handleSignOut = () => {
        // Clear React state and local storage
        ls.remove('userAuth');
        ls.remove('userAccount');
        safeLocalStorage.removeItem('userAuth');
        safeLocalStorage.removeItem('userAccount');
        
        // After signing out, send returning users to Login rather than the
        // role-picker. They can still start a new signup from Login's
        // "Sign Up" link if needed.
        history.push({
            pathname: 'Login'
        });
    }

    return (
        <Page>
            <Container>
                <HeaderStatusBar buttonClassName={'logout-link'} buttonText={'Sign Out'} withArrow onButton={handleSignOut} />

                <BodyContainer>
                    <SignUp1Header
                        title="Let's get to know your office..."
                        description="Dental Office Registration"
                        error={screenError ? 'Please fix the errors below' : undefined}
                    />

                    <form id="signup-form" data-testid="signup-office-contact-form" className="content-block contentBlock" onSubmit={onSubmit}>
                        <NameBlock className="list-block inset">
                            <MaterialInput
                                inputRef={htmlElRef}
                                label="Practice Name"
                                type={"text"}
                                id={'practiceName'}
                                name={"practiceName"}
                                defaultValue={state?.practiceName || ''}
                                error={!!errorMessage?.practiceName}
                                helperText={!!errorMessage?.practiceName && errorMessage.practiceName}
                            />
                            <MaterialInput
                                label="Business Owner's Full Name"
                                id={'ownersName'}
                                name={"ownersName"}
                                type={"text"}
                                defaultValue={state?.ownersName || ''}
                                error={!!errorMessage?.ownersName}
                                helperText={!!errorMessage?.ownersName && errorMessage.ownersName}
                            />
                        </NameBlock>

                        <CountrySelectBlock className="list-block inset">
                            <HeaderCountrySelect>Where are you based?</HeaderCountrySelect>
                            <BodyCountrySelect>
                                <FormControlLabel
                                    className={'no-fastclick'}
                                    control={<Checkbox
                                        icon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                   src={"img/dotCheck_inactive.svg"}/>}
                                        checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                          src={"img/dotCheck_active.svg"}/>}
                                        name="ca"
                                        checked={country === 0}
                                        onChange={changeCountry}
                                    />}
                                    label="Canada"
                                />
                                <FormControlLabel
                                    className={'no-fastclick'}
                                    control={<Checkbox
                                        icon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                   src={"img/dotCheck_inactive.svg"}/>}
                                        checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt=""
                                                          src={"img/dotCheck_active.svg"}/>}
                                        name="us"
                                        checked={country === 1}
                                        onChange={changeCountry}
                                    />}
                                    label="USA"
                                />
                            </BodyCountrySelect>
                            <div className={'inputTextField'}>
                                <div id="agreed-error-msg" className={'errorMsg'}>
                                    {errorMessage?.country}
                                </div>
                            </div>
                        </CountrySelectBlock>

                        <div className="list-block inset inputBox">
                            <MaterialInput
                                className={'no-fastclick'}
                                label="Start typing your address ..."
                                id={'autocomplete'}
                                name={"address"}
                                type={"text"}
                                value={addressInput}
                                onChange={handleAddressInputChange}
                                error={!!errorMessage?.address}
                                helperText={!!errorMessage?.address && errorMessage.address}
                            />
                        </div>

                        <div className="list-block inset inputBox halfWidth">
                            <MaterialInput
                                label="Unit / Suite (optional)"
                                id="unit-suite"
                                name="unitSuite"
                                type="text"
                                value={unitSuite}
                                onChange={(event) => setUnitSuite(event.target.value)}
                                inputProps={{
                                    'data-testid': 'office-unit-suite',
                                } as any}
                            />
                        </div>

                        <div className="list-block inset inputBox halfWidth">
                            <MaterialInput
                                label="Postal / ZIP Code"
                                id="postal-code"
                                name="postalCode"
                                type="text"
                                value={postalCode}
                                onChange={(event) => {
                                    setPostalCode(event.target.value.toUpperCase());
                                    setErrorMessage((current) => current ? {...current, address: undefined} : current);
                                }}
                                error={!!errorMessage?.postalCode}
                                helperText={errorMessage?.postalCode}
                                inputProps={{
                                    'data-testid': 'office-postal-code',
                                } as any}
                            />
                        </div>

                        <InputPhoneBlock className="list-block inset">
                            <MaterialInput
                                label="Office Phone Number"
                                id={'phone'}
                                name={'phone'}
                                type={'tel'}
                                value={phoneInput}
                                onChange={(event) => setPhoneInput(formatOfficePhoneWhileTyping(event.target.value))}
                                error={!!errorMessage?.phone}
                                helperText={!!errorMessage?.phone && errorMessage.phone}
                                inputProps={{inputMode: 'numeric', maxLength: 16} as any}
                            />
                        </InputPhoneBlock>

                        <div className="list-block inset">
                            <MaterialInput
                                label="Are you part of any DSO?"
                                value={dsoAffiliation}
                                onChange={(e) => setDsoAffiliation(e.target.value)}
                                name="dsoAffiliation"
                                id="dso-affiliation"
                                type="text"
                                inputProps={{
                                    placeholder: "Let us know here"
                                } as any}
                            />
                        </div>

                        <div className="list-block inset">
                            <FormControl fullWidth>
                                <MaterialInput
                                    select
                                    className={'no-fastclick'}
                                    label="How did you hear about us?"
                                    value={marketingSource}
                                    onChange={(e) => {
                                        setMarketingSource(e.target.value);
                                    }}
                                    error={!!errorMessage?.marketingSource}
                                    helperText={errorMessage?.marketingSource}
                                    name="marketingSource"
                                    id="marketing-source"
                                >
                                    {MARKETING_SOURCES.map((source) => (
                                        <MenuItem key={source.value} value={source.value}>
                                            {source.label}
                                        </MenuItem>
                                    ))}
                                </MaterialInput>
                            </FormControl>
                        </div>

                        {marketingSource === 'other' && (
                            <div className="list-block inset">
                                <MaterialInput
                                    label="Please specify"
                                    value={otherSource}
                                    onChange={(e) => setOtherSource(e.target.value)}
                                    error={!!errorMessage?.otherSource}
                                    helperText={errorMessage?.otherSource}
                                    name="otherSource"
                                />
                            </div>
                        )}

                        <ContinueButton
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            size="large"
                            id="signup-button"
                            type={'submit'}
                        >
                            Continue
                        </ContinueButton>
                        <ChangeRoleBlock>Not a Dental Office? <a onClick={changeRoleToTalent}>Find temping and job opportunities for dental professionals here</a></ChangeRoleBlock>
                    </form>
                </BodyContainer>
                <div className="footer">
                </div>
            </Container>
            <FixedLoader loading={changeRoleLoading || loading}/>
        </Page>
    )
}
